import { Button } from '~/elements/Button'
import { PageContent } from '~/elements/PageHeader'
import { TextField } from '~/elements/Input'
import { useCreateProjectMutation } from '~/features/projects/hooks/useProjectMutations'
import { ArrowRight, SparklesIcon, Database } from 'lucide-react'
import { useForm, z } from '~/lib/form'
import { getRoutePath } from '~/lib/router'
import { cn } from '~/lib/utils'
import { useCurrentWorkspaceQuery } from '~/features/workspaces/hooks/useWorkspaceQueries'
import { useWorkspaceProjectsQuery } from '~/features/workspaces/hooks/useWorkspaceQueries'

import { usePlatformSettings } from '~/features/auth/hooks/useAuthQueries'
import { useMemo } from 'react'
import { isPaidPlan } from '~/features/billing/utils'
import { openBillingBlockedDialog } from '~/features/billing/stores/blocked'

// Type for form values
type ProjectFormValues = {
  name: string
  description?: string
}

// Schema for a new project
const createProjectSchema = z.object({
  description: z.string().optional(),
  name: z.string().min(1).max(256)
})

function CreateProjectForm({ className, ...props }: TPolymorphicComponentProps<'form'>) {
  const { mutateAsync: mutate } = useCreateProjectMutation()
  const { data: workspace } = useCurrentWorkspaceQuery()
  const { data: platformSettings } = usePlatformSettings()
  const { data: projects, isFetching: isProjectsFetching } = useWorkspaceProjectsQuery()
  const maxProjects = workspace?.projectLimit ?? null
  const showUpgradeButton = useMemo(() => {
    if (isProjectsFetching) {
      return false
    }

    return !platformSettings?.selfHosted && maxProjects !== null && typeof projects !== 'undefined' ?
        (projects?.length ?? 0) >= maxProjects
      : false
  }, [platformSettings, maxProjects, projects, isProjectsFetching])

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register
  } = useForm<ProjectFormValues>({
    defaultValues: { description: '', name: '' },
    schema: createProjectSchema
  })

  const handleFormSubmit = (data: ProjectFormValues) => {
    const projectData = {
      name: data.name,
      description: data.description
    }
    return mutate(projectData)
  }

  return (
    <>
      <PageContent contained>
        <div className="mx-auto w-full max-w-2xl">
          <div className="mb-8 flex items-center gap-3 pt-10">
            <h1 className="text-2xl font-bold">Create New Project</h1>
          </div>
          <form
            {...props}
            className={cn('flex flex-col gap-6', className)}
            onSubmit={handleSubmit(handleFormSubmit)}
            autoComplete="off"
          >
            {/* Data plane (informational — all projects run on the RushDB data plane) */}
            <div className="space-y-2">
              <p className="text-content-secondary text-sm font-medium tracking-wide">Data plane</p>
              <div className="grid gap-3 md:grid-cols-1">
                <div className="border-border bg-surface-secondary flex h-full flex-col items-start rounded-lg border p-4 text-left">
                  <div className="mb-3 flex w-full items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Database className="h-4 w-4" />
                    </div>
                  </div>
                  <h3 className="mb-1 text-lg leading-snug font-semibold">
                    Hosted Database <span className="font-normal text-content2">[Fully Managed]</span>
                  </h3>
                  <p className="text-sm text-content2">
                    Every project runs on the RushDB managed data plane. Zero setup required.
                  </p>
                </div>
              </div>
            </div>

            {/* Basic Fields */}
            <div className="grid gap-4">
              <TextField
                label="Name"
                {...register('name')}
                autoFocus
                error={errors?.name?.message}
                data-tour="project-name-input"
                autoComplete="off"
                disabled={showUpgradeButton || isSubmitting}
              />
              <TextField
                label="Description (optional)"
                {...register('description')}
                error={errors?.description?.message}
                autoComplete="off"
                disabled={showUpgradeButton || isSubmitting}
              />
            </div>

            {showUpgradeButton && (
              <div className="bg-surface-secondary rounded-lg border border-accent/20 p-4 text-sm">
                <p className="mb-3 text-content2">
                  You've reached the maximum number of projects for your current plan.
                </p>
                {isPaidPlan(workspace?.planId) ?
                  <Button
                    onClick={() => openBillingBlockedDialog('project_limit')}
                    variant="primary"
                    className="w-full justify-center"
                  >
                    <SparklesIcon className="h-4 w-4" /> Buy an extra project
                  </Button>
                : <Button
                    as="a"
                    href={getRoutePath('workspaceBilling')}
                    variant="primary"
                    className="w-full justify-center"
                  >
                    <SparklesIcon className="h-4 w-4" /> Upgrade Plan
                  </Button>
                }
              </div>
            )}

            <div className="flex items-center justify-end gap-4">
              <Button as="a" href={getRoutePath('projects')} variant="secondary">
                Cancel
              </Button>
              <Button
                loading={isSubmitting}
                type="submit"
                variant="primary"
                data-tour="create-project-btn"
                disabled={showUpgradeButton}
                title={showUpgradeButton ? 'Upgrade plan to create more projects' : undefined}
              >
                Create Project <ArrowRight className="mr-1 h-4 w-4" />
              </Button>
            </div>
          </form>
        </div>
      </PageContent>
    </>
  )
}

export function NewProjectPage() {
  return (
    <div className="flex flex-1 flex-col">
      <CreateProjectForm />
    </div>
  )
}
